/**
 * Bulk MatchAction helpers for Component Matching Import (Product-PRDs #400).
 * Rewrites MatchAction on selected rows in memory — no CSV re-upload.
 * Local `action` / `manualPay` / `amountMode` stay derived via
 * `deriveRowDecisions` (same seam as the row-edit modal). The agent wire
 * sends `matchAction` only (#406).
 */
import { deriveRowDecisions } from "./deriveRowDecisions";
import type { MatchingCsvAction, MatchingRow } from "../types/matching";

export interface BulkMatchActionSummary {
  /** Rows whose MatchAction (or amount) will change. */
  changed: number;
  /** Rows whose TargetAmount will be blanked (DELETE / DELETE_KEEP_AMOUNT / REAPPLY). */
  amountCleared: number;
  /** Rows set to APPLY that currently have no TargetAmount. */
  missingAmount: number;
}

function indexSet(
  indices: ReadonlySet<number> | readonly number[]
): Set<number> {
  return indices instanceof Set ? indices : new Set(indices);
}

/**
 * Apply a MatchAction to the given 0-based row indices.
 * - APPLY: leave TargetAmount unchanged (missing amounts surface on re-validate).
 * - REAPPLY / DELETE_KEEP_AMOUNT / DELETE: blank TargetAmount (parser rules).
 */
export function applyBulkMatchAction(
  rows: MatchingRow[],
  indices: ReadonlySet<number> | readonly number[],
  action: MatchingCsvAction
): MatchingRow[] {
  const selected = indexSet(indices);
  if (selected.size === 0) return rows;

  const blankAmount =
    action === "DELETE" ||
    action === "DELETE_KEEP_AMOUNT" ||
    action === "REAPPLY";

  return rows.map((row, index) => {
    if (!selected.has(index)) return row;

    const targetAmount = blankAmount ? null : row.targetAmount;
    const rawAmount = blankAmount ? "" : row.rawAmount;
    const decisions = deriveRowDecisions(action, targetAmount);

    return {
      ...row,
      matchAction: action,
      rawMatchAction: action,
      targetAmount,
      rawAmount,
      manualPay: decisions.manualPay,
      amountMode: decisions.amountMode,
      action: decisions.action,
    };
  });
}

/** Counts for the bulk-action confirm modal. */
export function summariseBulkMatchAction(
  rows: MatchingRow[],
  indices: ReadonlySet<number> | readonly number[],
  action: MatchingCsvAction
): BulkMatchActionSummary {
  const selected = indexSet(indices);
  let changed = 0;
  let amountCleared = 0;
  let missingAmount = 0;

  const blankAmount =
    action === "DELETE" ||
    action === "DELETE_KEEP_AMOUNT" ||
    action === "REAPPLY";

  for (const index of selected) {
    const row = rows[index];
    if (!row) continue;

    const willClearAmount = blankAmount && row.targetAmount !== null;
    const actionChanges = row.matchAction !== action;
    if (actionChanges || willClearAmount) {
      changed += 1;
    }
    if (willClearAmount) {
      amountCleared += 1;
    }
    if (action === "APPLY" && row.targetAmount === null) {
      missingAmount += 1;
    }
  }

  return { changed, amountCleared, missingAmount };
}
